import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateUserProfile, deleteAccount } from "../../features/authSlice";
import { authService } from "../../services/authService";
import { validatePasswordStrength } from "../../utils/passwordValidation";
import styles from "./member.module.scss";

const Member = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!authService.isAuthenticated()) {
        navigate("/connect");
        return;
      }

      try {
        const userData = await authService.getUserProfile();
        setFormData((prev) => ({
          ...prev,
          username: userData.username || "",
          email: userData.email || "",
        }));
      } catch (error) {
        console.error("Erreur lors du chargement du profil:", error);
        if (error.response?.status === 401) {
          navigate("/connect");
        } else {
          setErrors((prev) => ({
            ...prev,
            load: "Impossible de charger les informations du profil",
          }));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Le nom d'utilisateur est requis";
    } else if (formData.username.length < 3) {
      newErrors.username =
        "Le nom d'utilisateur doit contenir au moins 3 caractères";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }

    if (formData.newPassword) {
      const pwdCheck = validatePasswordStrength(formData.newPassword);
      if (!pwdCheck.valid) {
        newErrors.newPassword = pwdCheck.message;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
      if (!formData.currentPassword) {
        newErrors.currentPassword =
          "Le mot de passe actuel est requis pour changer le mot de passe";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const updates = {
        username: formData.username,
        email: formData.email,
      };

      if (formData.newPassword) {
        updates.currentPassword = formData.currentPassword;
        updates.newPassword = formData.newPassword;
      }

      const updatedUser = await dispatch(updateUserProfile(updates)).unwrap();
      setSuccessMessage("Profil mis à jour avec succès");

      // Mettre à jour les données du formulaire avec les données retournées
      setFormData((prev) => ({
        ...prev,
        username: updatedUser.username || prev.username,
        email: updatedUser.email || prev.email,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit:
          error.message || "Une erreur est survenue lors de la mise à jour",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(deleteAccount()).unwrap();
      localStorage.removeItem("token");
      navigate("/connect", { replace: true });
    } catch (error) {
      console.error("Erreur lors de la suppression du compte:", error);
      setErrors((prev) => ({
        ...prev,
        delete:
          error.message ||
          "Une erreur est survenue lors de la suppression du compte",
      }));
      setShowDeleteConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className={styles.loading}>Chargement...</div>;
  }

  return (
    <div className={styles.memberPage}>
      <h1>Modifier mes infos</h1>

      {errors.load && <div className={styles.errorMessage}>{errors.load}</div>}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="username">Nom d'utilisateur</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className={errors.username ? styles.errorInput : ""}
          />
          {errors.username && (
            <span className={styles.errorText}>{errors.username}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? styles.errorInput : ""}
          />
          {errors.email && (
            <span className={styles.errorText}>{errors.email}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="currentPassword">Mot de passe actuel</label>
          <input
            type="password"
            id="currentPassword"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            className={errors.currentPassword ? styles.errorInput : ""}
          />
          {errors.currentPassword && (
            <span className={styles.errorText}>{errors.currentPassword}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="newPassword">Nouveau mot de passe</label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            className={errors.newPassword ? styles.errorInput : ""}
          />
          {errors.newPassword && (
            <span className={styles.errorText}>{errors.newPassword}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={errors.confirmPassword ? styles.errorInput : ""}
          />
          {errors.confirmPassword && (
            <span className={styles.errorText}>{errors.confirmPassword}</span>
          )}
        </div>

        {successMessage && (
          <div className={styles.successMessage}>{successMessage}</div>
        )}
        {errors.submit && (
          <div className={styles.errorMessage}>{errors.submit}</div>
        )}

        <div className={styles.buttonGroup}>
          <button
            type="submit"
            disabled={isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting ? "Mise à jour..." : "Mettre à jour"}
          </button>
        </div>
      </form>

      <div className={styles.deleteSection}>
        <h2>Supprimer mon compte</h2>
        <p>
          Attention : Cette action est irréversible. Toutes vos données seront
          définitivement supprimées.
        </p>
        {errors.delete && (
          <div className={styles.errorMessage}>{errors.delete}</div>
        )}
        <button
          onClick={handleDeleteAccount}
          disabled={isSubmitting}
          className={styles.deleteButton}
        >
          {showDeleteConfirm
            ? isSubmitting
              ? "Suppression..."
              : "Confirmer la suppression"
            : "Supprimer mon compte"}
        </button>
        {showDeleteConfirm && !isSubmitting && (
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className={styles.cancelButton}
          >
            Annuler
          </button>
        )}
      </div>
    </div>
  );
};

export default Member;
